// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

const SATS_PER_BTC = 100_000_000;

/** Format satoshis as a BTC string (e.g. 1_000_000 → "0.01000000"). */
export function formatBtc(sats: number): string {
  return (sats / SATS_PER_BTC).toFixed(8);
}

/** Format satoshis with locale grouping (e.g. 1_000_000 → "1,000,000"). */
export function formatSats(sats: number): string {
  return Math.round(sats).toLocaleString('en-US');
}

/** Parse a BTC string to satoshis. Returns NaN for invalid input. */
export function parseBtcToSats(btc: string): number {
  const n = parseFloat(btc);
  if (!isFinite(n) || n < 0) return NaN;
  return Math.round(n * SATS_PER_BTC);
}

/** Format a decimal fraction as a percentage (e.g. 0.10 → "10.0%"). */
export function formatPercent(decimal: number, decimals = 1): string {
  return `${(decimal * 100).toFixed(decimals)}%`;
}

/** Format a USD amount with dollar sign and grouping (e.g. 100000 → "$100,000"). */
export function formatUsd(amount: number): string {
  return `$${Math.round(amount).toLocaleString('en-US')}`;
}

/** Format a unix timestamp (seconds) as a short date string (e.g. "Jun 15, 2026"). */
export function formatDate(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Format a positive duration (seconds) as a compact "Xd Yh" / "Yh Zm"
 *  countdown. Under an hour shows just "Zm"; a non-positive or non-finite
 *  input renders the em-dash placeholder. Used by the COB-261 portfolio
 *  summary's NEXT SETTLEMENT figure. Pure — exposed for unit tests. */
export function formatCountdownShort(secondsRemaining: number): string {
  if (!Number.isFinite(secondsRemaining) || secondsRemaining <= 0) return '—';
  const totalMinutes = Math.floor(secondsRemaining / 60);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/** "Jun 15" style — used in deal-tile risk one-liners across home + accept.
 *  Falls back to the raw string if parsing fails so a malformed
 *  maturity_date still surfaces something the user can read. */
export function formatMaturityShort(maturityDate: string): string {
  const d = new Date(`${maturityDate}T00:00:00Z`);
  if (isNaN(d.getTime())) return maturityDate;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** Truncate a string to maxLen chars with ellipsis. */
export function truncate(s: string, maxLen: number): string {
  if (s.length <= maxLen) return s;
  return s.slice(0, maxLen) + '…';
}

/**
 * F16: per-period reward fraction for an offer. Mirror of the backend
 * `parse_offer_terms` formula in services/user-app/src/lib.rs — keep the
 * two in sync. The user's reward equals `yield_apy * days/365 * principal`.
 */
export function periodRewardFraction(yieldApy: number, daysToExpiry: number): number {
  return yieldApy * (daysToExpiry / 365);
}

/**
 * Reward in sats for a given principal under the offer terms. Backend
 * rounds the same way (`(yield_apy * days/365 * principal_sats).round()`).
 */
export function expectedRewardSats(
  principalSats: number,
  yieldApy: number,
  daysToExpiry: number,
): number {
  return Math.round(principalSats * periodRewardFraction(yieldApy, daysToExpiry));
}
